import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search as SearchIcon, X, SlidersHorizontal } from 'lucide-react';
import AdSlot from '../components/AdSlot';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_AVATAR = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMDAgMjAwIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzFBMUQyNCIvPjwvc3ZnPg==';

const CHARACTER_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'oc', label: 'OC', tagMatch: ['original', 'oc', 'original character'] },
  { id: 'fan-made', label: 'Fan-made', tagMatch: ['fan', 'fanmade', 'fan-made'] },
  { id: 'anime', label: 'Anime', tagMatch: ['anime', 'manga', 'manhwa'] },
  { id: 'horror', label: 'Horror', tagMatch: ['horror', 'eldritch', 'monster', 'yandere'] },
  { id: 'romance', label: 'Romance', tagMatch: ['romance', 'love', 'cute', 'devoted'] },
];

export default function Search() {
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('characters'); // 'characters' | 'users'
  const [activeFilter, setActiveFilter] = useState('all');
  const [characters, setCharacters] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 1) {
      setCharacters([]);
      setUsers([]);
      setHasSearched(false);
      return;
    }
    setLoading(true);
    setHasSearched(true);
    try {
      const [cRes, uRes] = await Promise.all([
        axios.get(`${API_URL}/api/characters`, { params: { search: q.trim(), limit: 40 } }),
        axios.get(`${API_URL}/api/users/search`, { params: { q: q.trim() } }),
      ]);
      setCharacters(cRes.data || []);
      setUsers(uRes.data || []);
    } catch (err) {
      console.error('Search failed:', err);
      setCharacters([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => runSearch(query), 280);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  const filteredCharacters = (() => {
    if (activeFilter === 'all') return characters;
    const filterDef = CHARACTER_FILTERS.find(f => f.id === activeFilter);
    if (!filterDef?.tagMatch) return characters;
    const lcMatches = filterDef.tagMatch.map(t => t.toLowerCase());
    return characters.filter(c => {
      const tags = (c.tags || []).map(t => String(t).toLowerCase());
      return tags.some(t => lcMatches.some(m => t.includes(m)));
    });
  })();

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="search-page">
      {/* Search bar */}
      <div
        className="sticky top-12 lg:top-0 z-30 bg-[#050505] px-4 pb-3 pt-2"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}
      >
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search characters and users"
              className="w-full h-12 pl-11 pr-10 bg-[#1A1D24] border border-white/5 rounded-2xl text-white placeholder-[#71717A] focus:outline-none focus:border-white/20 transition-colors text-base"
              data-testid="search-input"
              autoFocus
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center"
                data-testid="search-clear"
                aria-label="Clear"
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            )}
          </div>
          {query && (
            <button
              onClick={() => setQuery('')}
              className="text-white text-sm font-medium px-1"
              data-testid="search-cancel"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Tabs: Characters | Users */}
      <div className="px-4 pt-2 border-b border-white/5 flex gap-6">
        <button
          onClick={() => setTab('characters')}
          className={`relative pb-2 text-base font-bold transition-colors ${tab === 'characters' ? 'text-white' : 'text-[#71717A] hover:text-white/80'}`}
          data-testid="search-tab-characters"
        >
          Characters
          {tab === 'characters' && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#8B5CF6] rounded-full" />}
        </button>
        <button
          onClick={() => setTab('users')}
          className={`relative pb-2 text-base font-bold transition-colors ${tab === 'users' ? 'text-white' : 'text-[#71717A] hover:text-white/80'}`}
          data-testid="search-tab-users"
        >
          Users
          {tab === 'users' && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#8B5CF6] rounded-full" />}
        </button>
      </div>

      {/* Character filter pills (only on Characters tab) */}
      {tab === 'characters' && (
        <div className="px-4 py-3 flex items-center gap-2 overflow-x-auto scrollbar-hide" data-testid="filter-pills">
          {CHARACTER_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-bold border transition-all ${
                activeFilter === f.id
                  ? 'bg-[#8B5CF6] border-[#8B5CF6] text-white shadow-[0_0_18px_rgba(139,92,246,0.45)]'
                  : 'bg-transparent border-white/10 text-[#A1A1AA] hover:border-white/20 hover:text-white'
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
          <button
            onClick={() => setActiveFilter('all')}
            className="flex-shrink-0 ml-1 w-9 h-9 rounded-full bg-transparent border border-white/10 flex items-center justify-center text-[#A1A1AA] hover:text-white hover:border-white/20"
            aria-label="Reset filter"
            data-testid="filter-reset"
          >
            <SlidersHorizontal className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Results */}
      <div className="px-4 pb-24">
        {!hasSearched && (
          <div className="py-16 text-center text-[#71717A]">
            <SearchIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Search by character name or username</p>
          </div>
        )}

        {hasSearched && loading && (
          <div className="py-12 flex justify-center">
            <div className="w-6 h-6 border-2 border-[#8B5CF6] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {hasSearched && !loading && tab === 'characters' && (
          <>
            {filteredCharacters.length === 0 ? (
              <p className="py-12 text-center text-[#71717A] text-sm">No characters match &quot;{query}&quot;</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 pt-1">
                {filteredCharacters.map((c) => (
                  <Link
                    key={c.id}
                    to={`/character/${c.id}`}
                    className="block bg-[#1A1D24] border border-white/5 rounded-xl overflow-hidden hover:border-white/15 transition-colors"
                    data-testid={`result-char-${c.id}`}
                  >
                    <div className="aspect-[3/4] relative bg-black/20">
                      <img
                        src={c.avatar_url || DEFAULT_AVATAR}
                        alt={c.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                    </div>
                    <div className="p-2.5">
                      <p className="text-sm font-bold text-white truncate">{c.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-[#71717A] mt-0.5">
                        <span>{(c.chat_count || 0).toLocaleString()} chats</span>
                        {c.avg_rating ? <span>· {c.avg_rating.toFixed(1)}★</span> : null}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}

        {hasSearched && !loading && tab === 'users' && (
          <>
            {users.length === 0 ? (
              <p className="py-12 text-center text-[#71717A] text-sm">No users match &quot;{query}&quot;</p>
            ) : (
              <div className="space-y-2 pt-2">
                {users.map((u) => (
                  <Link
                    key={u.id}
                    to={`/user/${u.id}`}
                    className="flex items-center gap-3 p-3 bg-[#1A1D24] border border-white/5 rounded-xl hover:border-white/15 transition-colors"
                    data-testid={`result-user-${u.id}`}
                  >
                    <img
                      src={u.avatar || DEFAULT_AVATAR}
                      alt={u.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-white/10"
                      onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-bold text-white truncate">{u.name}</p>
                        {u.badge && (
                          <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-[#E11D48] text-white tracking-wider">{u.badge}</span>
                        )}
                      </div>
                      {u.bio && <p className="text-xs text-[#A1A1AA] truncate mt-0.5">{u.bio}</p>}
                      <p className="text-[10px] text-[#71717A] mt-0.5">{u.character_count} characters</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <AdSlot variant="sticky" />
    </div>
  );
}
